/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared DTOs for AI Token Consumption & Gross Margin Analytics (Task 11.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * 1. Model Token Usage & Unit Economics
 */
export const AdminModelTokenUsageSchema = z
  .object({
    modelId: z.string().openapi({ example: 'claude-3-5-sonnet', description: 'Internal model identifier' }),
    modelName: z.string().openapi({ example: 'Claude 3.5 Sonnet', description: 'Display name' }),
    provider: z.string().openapi({ example: 'anthropic', description: 'Upstream AI provider' }),
    inputTokens: z.number().openapi({ example: 12500000, description: 'Total prompt/input tokens' }),
    outputTokens: z.number().openapi({ example: 6400000, description: 'Total completion/output tokens' }),
    totalTokens: z.number().openapi({ example: 18900000, description: 'Combined token volume' }),
    estimatedProviderCost: z.number().openapi({ example: 133.5, description: 'Actual wholesale cost in USD' }),
    creditsDeducted: z.number().openapi({ example: 520000, description: 'Total wallet credits deducted' }),
    billedRevenue: z.number().openapi({ example: 520.0, description: 'Retail revenue equivalent in USD' }),
    grossProfit: z.number().openapi({ example: 386.5, description: 'Gross profit in USD' }),
    grossMargin: z.number().openapi({ example: 74.3, description: 'Gross margin percentage' }),
  })
  .openapi('AdminModelTokenUsage');

export type AdminModelTokenUsage = z.infer<typeof AdminModelTokenUsageSchema>;

/**
 * 2. Usage by Creation Modality
 */
export const AdminCreationTypeUsageSchema = z
  .object({
    type: z.string().openapi({ example: 'text_writer', description: 'Creative operation type' }),
    label: z.string().openapi({ example: 'AI Longform Writer', description: 'Display label' }),
    tokensOrUnits: z.number().openapi({ example: 14200000, description: 'Total tokens or media units' }),
    creditsSpent: z.number().openapi({ example: 412000, description: 'Total credits deducted' }),
    revenueEquivalent: z.number().openapi({ example: 412.0, description: 'Dollar value equivalent' }),
    percentage: z.number().openapi({ example: 42.5, description: 'Percentage of overall platform usage' }),
  })
  .openapi('AdminCreationTypeUsage');

export type AdminCreationTypeUsage = z.infer<typeof AdminCreationTypeUsageSchema>;

/**
 * 3. Executive Gross Margin Summary
 */
export const AdminGrossMarginSummarySchema = z
  .object({
    totalTokensIngested: z.number().openapi({ example: 48200000, description: 'Combined platform token volume' }),
    totalInputTokens: z.number().openapi({ example: 31400000, description: 'Total input tokens' }),
    totalOutputTokens: z.number().openapi({ example: 16800000, description: 'Total output tokens' }),
    totalProviderCost: z.number().openapi({ example: 348.6, description: 'Wholesale provider cost in USD' }),
    totalBilledRevenue: z.number().openapi({ example: 1240.0, description: 'Retail user credits revenue in USD' }),
    netGrossProfit: z.number().openapi({ example: 891.4, description: 'Net gross profit in USD' }),
    overallGrossMargin: z.number().openapi({ example: 71.9, description: 'Platform-wide gross margin percentage' }),
  })
  .openapi('AdminGrossMarginSummary');

export type AdminGrossMarginSummary = z.infer<typeof AdminGrossMarginSummarySchema>;

/**
 * 4. Token Consumption Timeline Datapoint
 */
export const AdminDailyTokenPointSchema = z
  .object({
    date: z.string().openapi({ example: '2026-10-01', description: 'Date label' }),
    tokens: z.number().openapi({ example: 1620000, description: 'Daily tokens consumed' }),
    providerCost: z.number().openapi({ example: 11.8, description: 'Daily provider cost in USD' }),
    billedRevenue: z.number().openapi({ example: 41.5, description: 'Daily retail revenue in USD' }),
    grossProfit: z.number().openapi({ example: 29.7, description: 'Daily gross profit in USD' }),
  })
  .openapi('AdminDailyTokenPoint');

export type AdminDailyTokenPoint = z.infer<typeof AdminDailyTokenPointSchema>;

/**
 * 5. Resource Abuse & Anomaly Warning
 */
export const AdminTokenAbuseWarningSchema = z
  .object({
    userId: z.string().openapi({ example: 'usr_abc123', description: 'User ID' }),
    userEmail: z.string().openapi({ example: 'spammer@domain.com', description: 'Account email' }),
    workspaceId: z.string().openapi({ example: 'ws_xyz789', description: 'Workspace ID' }),
    workspaceName: z.string().openapi({ example: 'Automated Bot Lab', description: 'Workspace display name' }),
    totalTokens24h: z.number().openapi({ example: 8900000, description: 'Tokens consumed in last 24h' }),
    totalRequests24h: z.number().openapi({ example: 4200, description: 'Requests count in last 24h' }),
    anomalyScore: z.number().openapi({ example: 86, description: 'Anomaly threat index (0-100)' }),
    status: z.enum(['flagged', 'normal', 'investigating']).openapi({ example: 'flagged' }),
  })
  .openapi('AdminTokenAbuseWarning');

export type AdminTokenAbuseWarning = z.infer<typeof AdminTokenAbuseWarningSchema>;

/**
 * 6. Complete Token Analytics Response
 */
export const AdminTokenAnalyticsResponseSchema = z
  .object({
    success: z.boolean().default(true),
    period: z.enum(['7d', '30d', '90d', '12m']),
    summary: AdminGrossMarginSummarySchema,
    modelBreakdown: z.array(AdminModelTokenUsageSchema),
    creationTypeBreakdown: z.array(AdminCreationTypeUsageSchema),
    dailyTimeline: z.array(AdminDailyTokenPointSchema),
    abuseAlerts: z.array(AdminTokenAbuseWarningSchema),
    generatedAt: z.string().openapi({ example: '2026-10-26T15:40:00Z' }),
  })
  .openapi('AdminTokenAnalyticsResponse');

export type AdminTokenAnalyticsResponse = z.infer<typeof AdminTokenAnalyticsResponseSchema>;
